import {useEffect, useRef, useState} from "react";
import {css} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {BrandLightBulbIcon, CloseIcon} from "@pg-design/icons-module";
import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {notifyBugsnag} from "../../../../errors/bugsnag/notify_bugsnag";
import {useUpdateForYouClientMutation} from "../../../../for_you/api/for_you_client";
import {setPriceConversationConsultant} from "../../../../lead/state/main_lead_slice";
import {getRandomConsultant} from "../../../../lead/utils/get_random_consultant";
import {addWarningNotification} from "../../../../notifications/utils/add_warning_notification";
import {OfferType} from "../../../../offer/types/OfferType";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {useGetUserProfileQuery} from "../../../../user/api/get_user_profile";
import {patchUserConsent} from "../../../../user/api/patch_user_consent";
import {PreferencesForm} from "../../../../user/components/PreferencesForm";
import {useIsAuthenticated} from "../../../../user/hooks/use_is_authenticated";
import {MarketingConsentType} from "../../../../user/types/MarketingConsentType";
import {useHomeMatchPreferencesStorage} from "../../../hooks/use_home_match_preferences_storage";
import {hitGtmSurveyBasicSubmit} from "../../../tracking/hit_gtm_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../tracking/hit_gtm_survey_click";
import {hitGtmSurveyClose} from "../../../tracking/hit_gtm_survey_close";
import {hitGtmSurveyView} from "../../../tracking/hit_gtm_survey_view";
import {IPreferencesDefaultValues} from "../../../types/IPreferencesDefaultValues";
import {ISetPreferencesStepPayload} from "../../../types/ISetPreferencesStepPayload";
import {PREFERENCES_BASE_VALUES, usePreferencesValues} from "../../../utils/get_preferences_values";
import {PreferencesFromProvider} from "./PreferencesFromProvider";
import {PreferencesModalHeading} from "./PreferencesModalHeading";
import {PreferencesSummary} from "./PreferencesSummary";

interface IProps {
    onClose: (payload: ISetPreferencesStepPayload) => void;
    applicationPayload: ISetPreferencesStepPayload;
    preferencesSurveySource: PreferencesSurveySource;
}

export const PreferencesModal = (props: IProps) => {
    const dispatch = useAppDispatch();
    const consultant = useAppSelector((state) => state.mainLead.consultant);
    const isAuthenticated = useIsAuthenticated();
    const {preferencesSentStorage, setPreferencesSent, putPendingPreferences} = useHomeMatchPreferencesStorage();
    const prefRef = useRef<IPreferencesDefaultValues>(PREFERENCES_BASE_VALUES);
    const {data: userProfile} = useGetUserProfileQuery();
    const showConsents = !!props.applicationPayload.propertyId && !userProfile?.email_marketing_consent && !userProfile?.phone_marketing_consent;

    const showForm =
        !preferencesSentStorage?.sent ||
        (preferencesSentStorage.sent && preferencesSentStorage.email !== props.applicationPayload.originalApplicationResponse?.email);

    const [formView, setFormView] = useState(showForm);
    const [hasAttemptedInitialSave, setHasAttemptedInitialSave] = useState(false);
    const [updateClient] = useUpdateForYouClientMutation();

    const multipleLeadsSend = props.applicationPayload.multiLeadSummaryItems && props.applicationPayload.multiLeadSummaryItems.length > 0;

    const {
        defaultValues: preferencesDefaultValues,
        isLoading: isLoadingValuesFromState,
        currency,
        maxBudget
    } = usePreferencesValues({
        creditPlan: props.applicationPayload.originalApplicationResponse?.financing_services || false,
        property: props.applicationPayload.propertyId,
        offer: props.applicationPayload.offerId
    });

    const defaultValues: IPreferencesDefaultValues = {
        ...preferencesDefaultValues,
        // Add marketing consents to default values if variant 1
        ...(showConsents && {
            marketing_email: userProfile?.email_marketing_consent ?? false,
            marketing_sms: userProfile?.phone_marketing_consent ?? false
        })
    };

    useEffect(() => {
        if (isLoadingValuesFromState) {
            return;
        }

        prefRef.current = defaultValues;
    }, [defaultValues]);

    useEffect(() => {
        if (!consultant) {
            dispatch(setPriceConversationConsultant(getRandomConsultant()));
        }
    }, [consultant]);

    useEffect(() => {
        if (formView && !isLoadingValuesFromState) {
            hitGtmSurveyView("homematch");
        }
    }, [formView, isLoadingValuesFromState]);

    useEffect(() => {
        const savePreferencesOnInit = async () => {
            if (isLoadingValuesFromState || hasAttemptedInitialSave || !isAuthenticated) {
                return;
            }

            setHasAttemptedInitialSave(true);
            const postData = transformPreferencesToPostData(defaultValues, maxBudget);

            try {
                await updateClient({...postData, survey_source: props.preferencesSurveySource}).unwrap();
                // Silent success - don't update UI or storage. User should still see the form to review/modify preferences
            } catch (error) {
                // Silent error - no user notification, log to Bugsnag for monitoring
                notifyBugsnag(error, "PreferencesModal initial save failed", {
                    isAuthenticated,
                    postData
                });
            }
        };

        void savePreferencesOnInit();
    }, [isLoadingValuesFromState, hasAttemptedInitialSave, isAuthenticated, defaultValues, maxBudget]);

    const onSubmit = async (values: IPreferencesDefaultValues) => {
        hitGtmSurveyBasicSubmit(values, "homematch");
        prefRef.current = values;

        const postData = transformPreferencesToPostData(values, maxBudget);

        try {
            if (isAuthenticated) {
                // For authenticated users, send directly to API
                await updateClient({...postData, survey_source: props.preferencesSurveySource}).unwrap();

                // Save marketing consents for variant 1 (FULL_OFFER) of AB test
                if (showConsents && (values.marketing_email || values.marketing_sms)) {
                    try {
                        await patchUserConsent({
                            [MarketingConsentType.EMAIL]: values.marketing_email ?? false,
                            [MarketingConsentType.PHONE]: values.marketing_sms ?? false
                        });
                    } catch (error) {
                        // Silent error - no user notification, log to Bugsnag for monitoring
                        notifyBugsnag(error, "PreferencesModal marketing consent save failed", {
                            isAuthenticated,
                            marketing_email: values.marketing_email,
                            marketing_sms: values.marketing_sms
                        });
                    }
                }
            } else {
                // Save postData to localStorage for unauthenticated users
                // They will be synced to API on the next login
                putPendingPreferences(postData);
            }
            setPreferencesSent(true, values, props.applicationPayload.originalApplicationResponse?.email);
            setFormView(false);
        } catch {
            setPreferencesSent(false, null, props.applicationPayload.originalApplicationResponse?.email);
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const onClose = () => {
        hitGtmSurveyClose("homematch");
        props.onClose(props.applicationPayload);
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick("homematch", value, section);
    };

    return (
        <div className={wrapper}>
            <div className={closeButton} onClick={onClose}>
                <CloseIcon size="1.6" wrapperSize="3.2" wrapperColor="#fff" />
            </div>
            {formView ? (
                isLoadingValuesFromState ? (
                    <CenterBox>
                        <Loader />
                    </CenterBox>
                ) : (
                    // We need to wait for the values to be set before setting the default values
                    <PreferencesFromProvider defaultValues={defaultValues}>
                        {(handleSubmit) => (
                            <PreferencesForm
                                handleSubmit={handleSubmit(onSubmit)}
                                header={<PreferencesModalHeading multipleLeadsSend={multipleLeadsSend ?? false} />}
                                currency={currency}
                                maxBudget={maxBudget}
                                onFieldInteraction={handleFieldInteraction}
                                showMarketingConsents={showConsents}
                                topFormContent={
                                    <div className={headerStyle}>
                                        <BrandLightBulbIcon size="3.6" wrapperSize="4.4" wrapperColor="var(--colors-primary)" />
                                        <div className={headerTextStyle}>
                                            <Text variant="headline_5" as="p">
                                                Potwierdź swoje preferencje
                                            </Text>
                                            <Text variant="body_copy_2">
                                                Z chęcią pomożemy Ci w poszukiwaniach wymarzonej nieruchomości oraz najlepszego kredytu do sfinansowania zakupu.
                                                Potrzebujemy doprecyzować kilka podstawowych parametrów poszukiwanej nieruchomości.
                                            </Text>
                                        </div>
                                    </div>
                                }
                            />
                        )}
                    </PreferencesFromProvider>
                )
            ) : (
                <PreferencesSummary preferences={prefRef.current} multipleLeadsSend={multipleLeadsSend ?? false} />
            )}
        </div>
    );
};

const transformPreferencesToPostData = (values: IPreferencesDefaultValues, maxBudget: number) => {
    const regions = values.regions.map((region) => region.value);

    const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : null;
    const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : null;
    let rooms = undefined;
    if (lower && upper) {
        rooms = {lower, upper};
    } else if (lower && !upper) {
        rooms = {lower, upper: 5};
    } else if (upper && !lower) {
        rooms = {lower: 1, upper};
    }

    return {
        regions: regions.length ? regions : undefined,
        rooms: rooms,
        types: [OfferType.FLAT, OfferType.HOUSE],
        price: values.budget ? {lower: 0, upper: values.budget === maxBudget ? MAX_VALUE_INPUT_LIMITER : values.budget} : undefined,
        financing_services: values.creditPlan
    };
};

const wrapper = css({
    position: "relative",
    backgroundColor: "#fff",

    md: {
        p: 3,
        overflow: "hidden"
    }
});

const closeButton = css({
    position: "absolute",
    top: 1.5,
    right: 1.5,
    cursor: "pointer",
    zIndex: 1,

    md: {
        top: 2,
        right: 2
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
import {useEffect, useRef, useState} from "react";
import {css} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {BrandLightBulbIcon, CloseIcon} from "@pg-design/icons-module";
import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {notifyBugsnag} from "../../../../errors/bugsnag/notify_bugsnag";
import {useUpdateForYouClientMutation} from "../../../../for_you/api/for_you_client";
import {setPriceConversationConsultant} from "../../../../lead/state/main_lead_slice";
import {getRandomConsultant} from "../../../../lead/utils/get_random_consultant";
import {addWarningNotification} from "../../../../notifications/utils/add_warning_notification";
import {OfferType} from "../../../../offer/types/OfferType";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {useGetUserProfileQuery} from "../../../../user/api/get_user_profile";
import {patchUserConsent} from "../../../../user/api/patch_user_consent";
import {PreferencesForm} from "../../../../user/components/PreferencesForm";
import {useIsAuthenticated} from "../../../../user/hooks/use_is_authenticated";
import {MarketingConsentType} from "../../../../user/types/MarketingConsentType";
import {useHomeMatchPreferencesStorage} from "../../../hooks/use_home_match_preferences_storage";
import {hitGtmSurveyBasicSubmit} from "../../../tracking/hit_gtm_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../tracking/hit_gtm_survey_click";
import {hitGtmSurveyClose} from "../../../tracking/hit_gtm_survey_close";
import {hitGtmSurveyView} from "../../../tracking/hit_gtm_survey_view";
import {IPreferencesDefaultValues} from "../../../types/IPreferencesDefaultValues";
import {ISetPreferencesStepPayload} from "../../../types/ISetPreferencesStepPayload";
import {PREFERENCES_BASE_VALUES, usePreferencesValues} from "../../../utils/get_preferences_values";
import {PreferencesFromProvider} from "./PreferencesFromProvider";
import {PreferencesModalHeading} from "./PreferencesModalHeading";
import {PreferencesSummary} from "./PreferencesSummary";

interface IProps {
    onClose: (payload: ISetPreferencesStepPayload) => void;
    applicationPayload: ISetPreferencesStepPayload;
    preferencesSurveySource: PreferencesSurveySource;
}

export const PreferencesModal = (props: IProps) => {
    const dispatch = useAppDispatch();
    const consultant = useAppSelector((state) => state.mainLead.consultant);
    const isAuthenticated = useIsAuthenticated();
    const {preferencesSentStorage, setPreferencesSent, putPendingPreferences} = useHomeMatchPreferencesStorage();
    const prefRef = useRef<IPreferencesDefaultValues>(PREFERENCES_BASE_VALUES);
    const {data: userProfile} = useGetUserProfileQuery();
    const showConsents = !!props.applicationPayload.propertyId && !userProfile?.email_marketing_consent && !userProfile?.phone_marketing_consent;

    const showForm =
        !preferencesSentStorage?.sent ||
        (preferencesSentStorage.sent && preferencesSentStorage.email !== props.applicationPayload.originalApplicationResponse?.email);

    const [formView, setFormView] = useState(showForm);
    const [hasAttemptedInitialSave, setHasAttemptedInitialSave] = useState(false);
    const [updateClient] = useUpdateForYouClientMutation();

    const multipleLeadsSend = props.applicationPayload.multiLeadSummaryItems && props.applicationPayload.multiLeadSummaryItems.length > 0;

    const {
        defaultValues: preferencesDefaultValues,
        isLoading: isLoadingValuesFromState,
        currency,
        maxBudget
    } = usePreferencesValues({
        creditPlan: props.applicationPayload.originalApplicationResponse?.financing_services || false,
        property: props.applicationPayload.propertyId,
        offer: props.applicationPayload.offerId
    });

    const defaultValues: IPreferencesDefaultValues = {
        ...preferencesDefaultValues,
        // Add marketing consents to default values if variant 1
        ...(showConsents && {
            marketing_email: userProfile?.email_marketing_consent ?? false,
            marketing_sms: userProfile?.phone_marketing_consent ?? false
        })
    };

    useEffect(() => {
        if (isLoadingValuesFromState) {
            return;
        }

        prefRef.current = defaultValues;
    }, [defaultValues]);

    useEffect(() => {
        if (!consultant) {
            dispatch(setPriceConversationConsultant(getRandomConsultant()));
        }
    }, [consultant]);

    useEffect(() => {
        if (formView && !isLoadingValuesFromState) {
            hitGtmSurveyView("homematch");
        }
    }, [formView, isLoadingValuesFromState]);

    useEffect(() => {
        const savePreferencesOnInit = async () => {
            if (isLoadingValuesFromState || hasAttemptedInitialSave || !isAuthenticated) {
                return;
            }

            setHasAttemptedInitialSave(true);
            const postData = transformPreferencesToPostData(defaultValues, maxBudget);

            try {
                await updateClient({...postData, survey_source: props.preferencesSurveySource}).unwrap();
                // Silent success - don't update UI or storage. User should still see the form to review/modify preferences
            } catch (error) {
                // Silent error - no user notification, log to Bugsnag for monitoring
                notifyBugsnag(error, "PreferencesModal initial save failed", {
                    isAuthenticated,
                    postData
                });
            }
        };

        void savePreferencesOnInit();
    }, [isLoadingValuesFromState, hasAttemptedInitialSave, isAuthenticated, defaultValues, maxBudget]);

    const onSubmit = async (values: IPreferencesDefaultValues) => {
        hitGtmSurveyBasicSubmit(values, "homematch");
        prefRef.current = values;

        const postData = transformPreferencesToPostData(values, maxBudget);

        try {
            if (isAuthenticated) {
                // For authenticated users, send directly to API
                await updateClient({...postData, survey_source: props.preferencesSurveySource}).unwrap();

                // Save marketing consents for variant 1 (FULL_OFFER) of AB test
                if (showConsents && (values.marketing_email || values.marketing_sms)) {
                    try {
                        await patchUserConsent({
                            [MarketingConsentType.EMAIL]: values.marketing_email ?? false,
                            [MarketingConsentType.PHONE]: values.marketing_sms ?? false
                        });
                    } catch (error) {
                        // Silent error - no user notification, log to Bugsnag for monitoring
                        notifyBugsnag(error, "PreferencesModal marketing consent save failed", {
                            isAuthenticated,
                            marketing_email: values.marketing_email,
                            marketing_sms: values.marketing_sms
                        });
                    }
                }
            } else {
                // Save postData to localStorage for unauthenticated users
                // They will be synced to API on the next login
                putPendingPreferences(postData);
            }
            setPreferencesSent(true, values, props.applicationPayload.originalApplicationResponse?.email);
            setFormView(false);
        } catch {
            setPreferencesSent(false, null, props.applicationPayload.originalApplicationResponse?.email);
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const onClose = () => {
        hitGtmSurveyClose("homematch");
        props.onClose(props.applicationPayload);
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick("homematch", value, section);
    };

    return (
        <div className={wrapper}>
            <div className={closeButton} onClick={onClose}>
                <CloseIcon size="1.6" wrapperSize="3.2" wrapperColor="#fff" />
            </div>
            {formView ? (
                isLoadingValuesFromState ? (
                    <CenterBox>
                        <Loader />
                    </CenterBox>
                ) : (
                    // We need to wait for the values to be set before setting the default values
                    <PreferencesFromProvider defaultValues={defaultValues}>
                        {(handleSubmit) => (
                            <PreferencesForm
                                handleSubmit={handleSubmit(onSubmit)}
                                header={<PreferencesModalHeading multipleLeadsSend={multipleLeadsSend ?? false} />}
                                currency={currency}
                                maxBudget={maxBudget}
                                onFieldInteraction={handleFieldInteraction}
                                showMarketingConsents={showConsents}
                                topFormContent={
                                    <div className={headerStyle}>
                                        <BrandLightBulbIcon size="3.6" wrapperSize="4.4" wrapperColor="var(--colors-primary)" />
                                        <div className={headerTextStyle}>
                                            <Text variant="headline_5" as="p">
                                                Potwierdź swoje preferencje
                                            </Text>
                                            <Text variant="body_copy_2">
                                                Z chęcią pomożemy Ci w poszukiwaniach wymarzonej nieruchomości oraz najlepszego kredytu do sfinansowania zakupu.
                                                Potrzebujemy doprecyzować kilka podstawowych parametrów poszukiwanej nieruchomości.
                                            </Text>
                                        </div>
                                    </div>
                                }
                            />
                        )}
                    </PreferencesFromProvider>
                )
            ) : (
                <PreferencesSummary preferences={prefRef.current} multipleLeadsSend={multipleLeadsSend ?? false} />
            )}
        </div>
    );
};

const transformPreferencesToPostData = (values: IPreferencesDefaultValues, maxBudget: number) => {
    const regions = values.regions.map((region) => region.value);

    const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : null;
    const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : null;
    let rooms = undefined;
    if (lower && upper) {
        rooms = {lower, upper};
    } else if (lower && !upper) {
        rooms = {lower, upper: 5};
    } else if (upper && !lower) {
        rooms = {lower: 1, upper};
    }

    return {
        regions: regions.length ? regions : undefined,
        rooms: rooms,
        types: [OfferType.FLAT, OfferType.HOUSE],
        price: values.budget ? {lower: 0, upper: values.budget === maxBudget ? MAX_VALUE_INPUT_LIMITER : values.budget} : undefined,
        financing_services: values.creditPlan
    };
};

const wrapper = css({
    position: "relative",
    backgroundColor: "#fff",

    md: {
        p: 3,
        overflow: "hidden"
    }
});

const closeButton = css({
    position: "absolute",
    top: 1.5,
    right: 1.5,
    cursor: "pointer",
    zIndex: 1,

    md: {
        top: 2,
        right: 2
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
